import { safeSetInnerHTML, safeInsertAdjacentHTML } from '@merlin/lib/js/trusted-types.js';
/**
 * GraphExplorer - Minimal Core
 * 
 * A lightweight vis.js network wrapper that delegates UI to modular components.
 * This file handles ONLY:
 * - vis.js network initialization
 * - Network options building (physics, layout, edges)
 * - Data management (nodes/edges)
 * - Public API methods
 * - Event coordination
 * 
 * UI Components (toolbar, windows, tabs, etc.) are handled by:
 * - explorer-core.js - Main UI controller
 * - explorer-toolbar.js - Toolbar rendering/handlers
 * - explorer-tabs.js - Tab navigation
 * - explorer-windows.js - Floating windows
 * - explorer-panels.js - Overlay panels
 * - explorer-api.js - API integration
 * - explorer-keyboard.js - Keyboard shortcuts
 * - etc.
 */

// ============================================
// CDN Loaders
// ============================================

async function loadVisJS() {
    if (typeof vis !== 'undefined') return;
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/vis-network@9.1.9/dist/vis-network.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
        
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cdn.jsdelivr.net/npm/vis-network@9.1.9/dist/dist/vis-network.min.css';
        document.head.appendChild(css);
    });
}

async function loadLucide() {
    if (typeof lucide !== 'undefined') return;
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
        script.onload = () => {
            setTimeout(() => lucide.createIcons(), 10);
            resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

// ============================================
// GraphExplorer Class
// ============================================

export class GraphExplorer {
    constructor(container, options = {}) {
        this.container = typeof container === 'string' 
            ? document.querySelector(container) 
            : container;
            
        // Merge with defaults from constants (if available)
        const defaults = window.ExplorerConstants?.DEFAULT_EXPLORER_OPTIONS || {};
        this.options = { ...defaults, ...options };
        
        // Network state
        this.network = null;
        this.nodes = null;
        this.edges = null;
        this.nodeMap = {};
        
        // Selection state
        this.state = {
            selectedNodes: [],
            selectedEdges: [],
            originalEdgeColors: {},
            originalNodeColors: {},  // Store original node colors for focus mode
            isLoading: false,
            focusMode: false  // Track if we're in focus mode (dimmed non-selected)
        };
        
        // Initialize
        this._initPromise = this._init();
    }
    
    // ============================================
    // Initialization
    // ============================================
    
    async _init() {
        // Load dependencies
        await Promise.all([loadVisJS(), loadLucide()]);
        
        // Create minimal DOM structure
        this._createDOM();
        
        // Initialize vis.js network
        this._initNetwork();
        
        // Notify modules that explorer is ready
        this._notifyReady();
        
        console.log('[GraphExplorer] Minimal core initialized');
    }
    
    async ready() {
        return this._initPromise;
    }
    
    _createDOM() {
        const extensionClass = this.options.extension_mode ? 'extension-mode' : '';
        const theme = this.options.theme || 'dark';
        
        // Create minimal shell - UI modules will inject their content
        safeSetInnerHTML(this.container, `
            <div class="graph-explorer ${extensionClass}${theme === 'light' ? ' light-theme' : ''}" data-theme="${theme}">
                <!-- Loading Overlay -->
                <div id="loadingBar" style="display: none;">
                    <svg class="loading-spinner" viewBox="0 0 50 50">
                        <circle class="loading-spinner-ring" cx="25" cy="25" r="20"></circle>
                        <circle class="loading-spinner-arc" cx="25" cy="25" r="20"></circle>
                    </svg>
                    <div class="loading-messages">
                        <span id="loadingBar-text">Loading...</span>
                        <div class="loading-step-dots">
                            <span class="loading-step-dot" data-step="0"></span>
                            <span class="loading-step-dot" data-step="1"></span>
                            <span class="loading-step-dot" data-step="2"></span>
                            <span class="loading-step-dot" data-step="3"></span>
                        </div>
                    </div>
                    <div id="loadingBar-progress"></div>
                </div>
                
                <!-- Empty State (shown when no nodes) -->
                <div id="empty-state-overlay" class="empty-state-overlay" style="display: none;">
                    <div class="empty-state-content">
                        <div class="empty-state-icon">
                            <i data-lucide="git-graph" style="width: 64px; height: 64px; stroke-width: 1.5;"></i>
                        </div>
                        <h2 class="empty-state-title">No nodes in graph</h2>
                        <p class="empty-state-description">Search for concepts and add them to build your knowledge graph</p>
                        <div class="empty-state-search-container">
                            <!-- EntityInput container for autocomplete search -->
                            <div id="empty-state-entity-input" class="empty-state-entity-input-container"></div>
                            <button id="empty-state-search-btn" class="empty-state-btn">
                                <i data-lucide="search" style="width: 18px; height: 18px;"></i>
                                Search
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Tab Bar Container (populated by explorer-tabs.js) -->
                <div id="explorer-tabs-container"></div>
                
                <!-- Main Graph Canvas -->
                <div id="mynetwork" class="graph-canvas"></div>
                
                <!-- Toolbar Container (populated by explorer-toolbar.js) -->
                <div id="toolbar-container"></div>
                
                <!-- Floating Windows Container (managed by explorer-windows.js) -->
                <div id="floating-windows" class="floating-windows"></div>
                
                <!-- Panel Overlay Container (managed by explorer-panels.js) -->
                <div id="panel-container"></div>
                
                <!-- Bottom Bar Container (managed by explorer-bottom-bar.js) -->
                <div id="bottom-bar-container"></div>
            </div>
        `);
        
        // Initialize Lucide icons
        if (typeof lucide !== 'undefined') {
            setTimeout(() => lucide.createIcons(), 10);
        }
        
        // Setup empty state search handlers
        this._setupEmptyStateSearch();
    }
    
    _setupEmptyStateSearch() {
        const entityInputContainer = this.container.querySelector('#empty-state-entity-input');
        const searchBtn = this.container.querySelector('#empty-state-search-btn');
        const emptyOverlay = this.container.querySelector('#empty-state-overlay');
        
        // Initialize EntityInput if available
        if (entityInputContainer && typeof EntityInput !== 'undefined') {
            const theme = this.options.theme || 'dark';
            this._emptyStateEntityInput = new EntityInput({
                placeholder: 'Search nodes, concepts, and relationships...',
                debounceMs: 300,
                minSearchLength: 2,
                theme: theme,
                dropdownPosition: 'below', // Dropdown below input so it's not cut off
                onSearch: (query) => this._handleEmptyStateAutocomplete(query),
                onSubmit: (data) => {
                    const query = data.query;
                    const keywords = data.keywords || [];
                    if (query || keywords.length > 0) {
                        this._executeEmptyStateSearch(query, keywords);
                    }
                },
                onEntitySelect: (entity) => {
                    console.log('[GraphExplorer] Entity selected:', entity);
                },
                onChange: (data) => {
                    // Optional: track changes
                },
            });
            this._emptyStateEntityInput.init(entityInputContainer);
        }
        
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                let query = '';
                let keywords = [];
                
                if (this._emptyStateEntityInput) {
                    query = this._emptyStateEntityInput.getFullQuery();
                    keywords = this._emptyStateEntityInput.getSelectedEntities();
                }
                
                if (query || keywords.length > 0) {
                    this._executeEmptyStateSearch(query, keywords);
                } else {
                    // If no query, open the Explorer window (Search & Find)
                    if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.toggle) {
                        ExplorerWindows.toggle('explorer');
                    }
                    if (emptyOverlay) emptyOverlay.style.display = 'none';
                }
            });
        }
    }
    
    async _handleEmptyStateAutocomplete(query) {
        // Check if we have API access for suggestions
        if (!query || query.length < 2) {
            if (this._emptyStateEntityInput) {
                this._emptyStateEntityInput.setSuggestions([]);
            }
            return;
        }
        
        // Try to get suggestions from API
        // Read from ExplorerAPI live config (updated on workspace switch) with fallback to options
        const liveConfig = (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getConfig)
            ? ExplorerAPI.getConfig() : {};
        const apiEndpoint = liveConfig.apiEndpoint ?? this.options.api_endpoint ?? this.options.apiEndpoint ?? '';
        const workspaceId = liveConfig.workspaceId || this.options.workspace_id || this.options.workspaceId;
        const authToken = this.options.auth_token || this.options.accessToken;
        
        if (!workspaceId) {
            // No workspace - no suggestions
            console.log('[GraphExplorer] No workspace ID for suggestions');
            return;
        }
        
        try {
            const url = `${apiEndpoint}/workspaces/${workspaceId}/search/suggest?query=${encodeURIComponent(query)}&limit=8`;
            console.log('[GraphExplorer] Fetching suggestions:', url);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
                }
            });
            
            if (response.ok) {
                const suggestions = await response.json();
                if (this._emptyStateEntityInput && suggestions && suggestions.length > 0) {
                    this._emptyStateEntityInput.setSuggestions(suggestions);
                } else if (this._emptyStateEntityInput) {
                    this._emptyStateEntityInput.setSuggestions([]);
                }
            }
        } catch (err) {
            console.warn('[GraphExplorer] Failed to load suggestions:', err);
            if (this._emptyStateEntityInput) {
                this._emptyStateEntityInput.setSuggestions([]);
            }
        }
    }
    
    _executeEmptyStateSearch(query, keywords = []) {
        const emptyOverlay = this.container.querySelector('#empty-state-overlay');
        
        // Build search params including selected entities
        const searchData = { query, keywords };
        
        // Use ExplorerAPI.searchAsk to analyze text and load into cursor
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.searchAsk) {
            console.log('[GraphExplorer] Executing search/ask:', searchData);
            
            // Show loading state
            const searchBtn = this.container.querySelector('#empty-state-search-btn');
            if (searchBtn) {
                searchBtn.disabled = true;
                safeSetInnerHTML(searchBtn, '<i data-lucide="loader-2" class="spin" style="width: 18px; height: 18px;"></i> Searching...');
            }
            
            // From empty state, always reset cursor first to start fresh
            ExplorerAPI.searchAsk(query, keywords, { resetCursor: false })
                .then((result) => {
                    console.log('[GraphExplorer] Search/ask completed:', result);
                    
                    // Hide empty state if entities were loaded
                    if (result.total_entities_loaded > 0 && emptyOverlay) {
                        emptyOverlay.style.display = 'none';
                    }
                    
                    // Show notification
                    if (result.total_entities_loaded > 0) {
                        this._showNotification(`Loaded ${result.total_entities_loaded} entities into graph`, 'success');
                    } else {
                        this._showNotification('No matching entities found', 'info');
                    }
                })
                .catch((err) => {
                    console.error('[GraphExplorer] Search/ask failed:', err);
                    this._showNotification(`Search failed: ${err.message}`, 'error');
                })
                .finally(() => {
                    // Restore button state
                    if (searchBtn) {
                        searchBtn.disabled = false;
                        safeSetInnerHTML(searchBtn, '<i data-lucide="search" style="width: 18px; height: 18px;"></i> Search');
                        if (typeof lucide !== 'undefined') {
                            lucide.createIcons({ nodes: [searchBtn] });
                        }
                    }
                });
        } 
        // Fallback: Try ExplorerModal (semantic search)
        else if (typeof ExplorerModal !== 'undefined' && ExplorerModal.executeSearch) {
            ExplorerModal.executeSearch(query, keywords);
            if (emptyOverlay) emptyOverlay.style.display = 'none';
        } 
        // Fallback: open Explorer window with the query
        else if (typeof ExplorerWindows !== 'undefined') {
            ExplorerWindows.toggle('explorer');
            // Try to populate the search input (in Find tab)
            setTimeout(() => {
                const findInput = document.getElementById('find-search');
                if (findInput) {
                    findInput.value = query;
                    findInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 100);
            if (emptyOverlay) emptyOverlay.style.display = 'none';
        }
        // Emit event for external handlers
        this._emitEvent('search', searchData);
        
        // Clear the EntityInput after search
        if (this._emptyStateEntityInput) {
            this._emptyStateEntityInput.clear();
        }
    }
    
    _initNetwork() {
        const networkContainer = this.container.querySelector('#mynetwork');
        if (!networkContainer) return;
        
        // Initialize DataSets
        this.nodes = new vis.DataSet([]);
        this.edges = new vis.DataSet([]);
        
        // Build network options
        const networkOptions = this._buildNetworkOptions();
        
        // Create network
        this.network = new vis.Network(
            networkContainer,
            { nodes: this.nodes, edges: this.edges },
            networkOptions
        );

        // ── Fit-call guard + chat-panel-aware centering ──────
        // vis.js emits internal "fit" events from initPhysics() on
        // every setOptions({ physics: ... }) call.  These are NOT
        // controlled by stabilization.fit and cause unwanted viewport
        // recentering.  We intercept network.fit() so that when
        // _suppressFit is true the call is silently dropped.
        //
        // When the chat panel is open the graph canvas covers only
        // 7 of 12 columns.  vis.js may still compute its viewport
        // centre from stale (full-width) canvas dimensions, causing
        // fit/focus to appear off-centre.  We compensate by doing a
        // silent fit (animation:false) to let vis.js compute the
        // correct scale, then immediately calling moveTo() with an
        // x-offset equal to −(chatPanelWidth / 2).
        this.network._suppressFit = false;
        const _origFit = this.network.fit.bind(this.network);
        this.network.fit = (...args) => {
            if (this.network._suppressFit) {
                return;
            }
            const offset = this._getChatPanelOffset();
            if (offset !== 0) {
                const opts = args[0] || {};
                const anim = opts.animation;
                // Silent fit → correct scale, wrong centre
                _origFit({ ...opts, animation: false });
                // Re-centre with chat-aware offset
                const pos = this.network.getViewPosition();
                const scale = this.network.getScale();
                this.network.moveTo({
                    position: pos,
                    scale: scale,
                    offset: { x: offset, y: 0 },
                    animation: anim !== undefined ? anim : false
                });
                return;
            }
            return _origFit(...args);
        };

        // ── Focus interceptor (chat-panel-aware) ──────────────
        // network.focus() centres on a single node.  Apply the same
        // chat-panel offset so the node appears in the middle of the
        // visible graph area, not the middle of the full viewport.
        const _origFocus = this.network.focus.bind(this.network);
        this.network.focus = (nodeId, options = {}) => {
            const offset = this._getChatPanelOffset();
            if (offset !== 0) {
                const positions = this.network.getPositions([nodeId]);
                const pos = positions[nodeId];
                if (!pos) return _origFocus(nodeId, options);
                this.network.moveTo({
                    position: pos,
                    scale: options.scale || this.network.getScale(),
                    offset: { x: offset, y: 0 },
                    animation: options.animation !== undefined ? options.animation : false,
                });
                return;
            }
            return _origFocus(nodeId, options);
        };

        // Store original edge colors when added
        this.edges.on('add', (event, properties) => {
            properties.items.forEach(edgeId => {
                const edge = this.edges.get(edgeId);
                if (edge && !this.state.originalEdgeColors[edgeId]) {
                    this.state.originalEdgeColors[edgeId] = {
                        color: typeof edge.color === 'string' ? edge.color : edge.color?.color || '#7f8c8d',
                        width: edge.width || 1.5
                    };
                }
            });
        });
        
        // Stabilization events — only log, never show the loading spinner.
        // The sequential loading UX ("Accessing the instance …") already covers
        // the user-facing loading state during fetchAndUpdate / searchAsk.
        // Showing "Stabilizing…" from vis.js physics was redundant and could
        // hang (stabilizationIterationsDone sometimes never fires in embedded
        // contexts or with 0 nodes), leaving a permanent spinner.
        this.network.on('startStabilizing', () => {
            console.log('[GraphExplorer] Physics stabilization started');
        });
        
        this.network.on('stabilizationIterationsDone', () => {
            console.log('[GraphExplorer] Physics stabilization complete');
        });
        
        this.network.on('stabilized', () => {
            console.log('[GraphExplorer] Network stabilized');
        });
        
        // Selection events
        // When onSelectionChange is provided, external modules (ExplorerCore) manage
        // the visual dimming/restoring, so skip the built-in focus mode to avoid conflicts.
        const externalSelectionManagement = typeof this.options.onSelectionChange === 'function';
        
        this.network.on('selectNode', (params) => {
            this.state.selectedNodes = params.nodes;
            if (!externalSelectionManagement) {
                this._applyFocusMode(params.nodes);
            }
            this._emitEvent('selectionChange', { nodes: params.nodes, edges: this.state.selectedEdges });
        });
        
        this.network.on('selectEdge', (params) => {
            this.state.selectedEdges = params.edges;
            this._emitEvent('selectionChange', { nodes: this.state.selectedNodes, edges: params.edges });
        });
        
        this.network.on('deselectNode', () => {
            this.state.selectedNodes = [];
            if (!externalSelectionManagement) {
                this._clearFocusMode();
            }
            this._emitEvent('selectionChange', { nodes: [], edges: this.state.selectedEdges });
        });
        
        this.network.on('deselectEdge', () => {
            this.state.selectedEdges = [];
            this._emitEvent('selectionChange', { nodes: this.state.selectedNodes, edges: [] });
        });
        
        // Click events
        this.network.on('click', (params) => {
            this._emitEvent('click', params);
        });
        
        this.network.on('doubleClick', (params) => {
            this._emitEvent('doubleClick', params);
            if (params.nodes.length > 0) {
                const nodeId = params.nodes[0];
                // If the double-clicked node is a cluster, expand it instead of extending
                if (typeof ExplorerClustering !== 'undefined' && ExplorerClustering.isCluster(nodeId)) {
                    ExplorerClustering.expandCluster(nodeId);
                } else if (this.options.onOperator) {
                    this.options.onOperator('extend', [nodeId]);
                }
            }
        });
    }
    
    _notifyReady() {
        // Emit ready event for modules to initialize
        this._emitEvent('ready', {
            explorer: this,
            network: this.network,
            nodes: this.nodes,
            edges: this.edges,
            container: this.container,
            options: this.options
        });
        
        // Also dispatch DOM event for non-IIFE modules
        const event = new CustomEvent('explorer:ready', {
            detail: {
                explorer: this,
                network: this.network,
                nodes: this.nodes,
                edges: this.edges
            }
        });
        this.container.dispatchEvent(event);
        document.dispatchEvent(event);
    }
    
    // ============================================
    // Event System
    // ============================================
    
    _eventListeners = {};
    
    on(event, callback) {
        if (!this._eventListeners[event]) {
            this._eventListeners[event] = [];
        }
        this._eventListeners[event].push(callback);
        return this;
    }
    
    off(event, callback) {
        if (this._eventListeners[event]) {
            this._eventListeners[event] = this._eventListeners[event].filter(cb => cb !== callback);
        }
        return this;
    }
    
    _emitEvent(event, data) {
        if (this._eventListeners[event]) {
            this._eventListeners[event].forEach(cb => cb(data));
        }
        
        // Also check for callback options
        const callbackName = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`;
        if (this.options[callbackName]) {
            this.options[callbackName](data);
        }
    }
    
    // ============================================
    // Network Options Builder
    // ============================================
    
    _buildNetworkOptions() {
        const opts = this.options;
        
        return {
            nodes: this._buildNodeOptions(),
            edges: this._buildEdgeOptions(),
            physics: this._buildPhysicsOptions(),
            layout: this._buildLayoutOptions(),
            interaction: this._buildInteractionOptions(),
            configure: { enabled: false }
        };
    }
    
    _buildNodeOptions() {
        const opts = this.options;
        // Disable shadows for large graphs — canvas composite ops are expensive
        const nodeCount = this.nodes ? this.nodes.length : 0;
        const shadowEnabled = opts.node_shadow !== undefined ? opts.node_shadow : (nodeCount < 500);
        return {
            shape: opts.node_shape || 'dot',
            size: opts.node_size || 16,
            font: {
                size: opts.node_font_size || 14,
                color: opts.theme === 'dark' ? '#ffffff' : '#212529',
                face: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            },
            borderWidth: opts.node_border_width || 2,
            shadow: shadowEnabled
        };
    }
    
    _buildEdgeOptions() {
        const opts = this.options;
        return {
            width: 1.5,
            color: {
                color: '#7f8c8d',
                highlight: '#888888',
                hover: '#888888',
                opacity: 1.0,
                inherit: opts.inherit_edge_colors || false
            },
            font: {
                size: 0,
                color: '#aaaaaa',
                strokeWidth: 0,
                align: 'middle'
            },
            smooth: {
                enabled: true,
                type: opts.edge_smooth || 'continuous'
            },
            arrows: opts.edge_arrows ? {
                to: { enabled: true, scaleFactor: opts.edge_arrow_scale || 0.5 }
            } : {}
        };
    }
    
    _buildPhysicsOptions() {
        const opts = this.options;
        const solver = opts.physics_solver || 'forceAtlas2Based';
        const physicsDefaults = window.ExplorerConstants?.DEFAULT_PHYSICS_OPTIONS || {};
        
        const config = {
            enabled: opts.physics_enabled !== false,
            timestep: 0.35,
            maxVelocity: 30,
            minVelocity: 0.75,
            stabilization: {
                enabled: opts.stabilization?.enabled !== false,
                iterations: opts.stabilization?.iterations || 50,
                updateInterval: opts.stabilization?.updateInterval || 100,
                fit: false  // Never auto-fit after stabilization — user controls the viewport
            },
            solver
        };
        
        // Add solver-specific options
        const solverOpts = opts.physics_options?.[solver] || physicsDefaults[solver] || {};
        config[solver] = solverOpts;
        
        return config;
    }
    
    _buildLayoutOptions() {
        const opts = this.options;
        
        const config = {
            improvedLayout: false,  // O(N²) overlap prevention — too slow for large graphs
            randomSeed: 0
        };
        
        if (opts.layout === 'hierarchical' || opts.hierarchical?.enabled) {
            config.hierarchical = {
                enabled: true,
                levelSeparation: opts.hierarchical?.levelSeparation || 150,
                treeSpacing: opts.hierarchical?.treeSpacing || 200,
                blockShifting: opts.hierarchical?.blockShifting !== false,
                edgeMinimization: opts.hierarchical?.edgeMinimization !== false,
                parentCentralization: opts.hierarchical?.parentCentralization !== false,
                direction: opts.hierarchical?.direction || 'UD',
                sortMethod: opts.hierarchical?.sortMethod || 'hubsize'
            };
        }
        
        return config;
    }
    
    _buildInteractionOptions() {
        const opts = this.options;
        return {
            hover: opts.hover !== false,
            dragNodes: opts.drag_nodes !== false,
            dragView: opts.drag_view !== false,
            zoomView: opts.zoom_view !== false,
            multiselect: opts.multiselect !== false,
            selectConnectedEdges: false,
            hideEdgesOnDrag: opts.hide_edges_on_drag || false,
            hideNodesOnDrag: opts.hide_nodes_on_drag || false,
            tooltipDelay: opts.tooltip_delay || 200
        };
    }
    
    // ============================================
    // Loading State
    // ============================================
    
    /**
     * Sequential loading messages shown during search/fetch operations.
     */
    static LOADING_STEPS = [
        'Accessing the instance ...',
        'Operating on cursor ...',
        'Fetching cursor state ...',
        'Polishing results ...',
    ];

    _showLoading(text = null, { sequential = false } = {}) {
        const loadingBar = this.container.querySelector('#loadingBar');
        const loadingText = this.container.querySelector('#loadingBar-text');
        const dots = this.container.querySelectorAll('.loading-step-dot');
        const dotsContainer = this.container.querySelector('.loading-step-dots');
        const progress = this.container.querySelector('#loadingBar-progress');
        // Hide empty-state overlay — loading takes priority over the "no nodes" screen
        const emptyState = this.container.querySelector('#empty-state-overlay');
        if (emptyState) emptyState.style.display = 'none';

        if (loadingBar) {
            loadingBar.classList.remove('hidden'); // Ensure checkEmptyState's "hidden" class is cleared
            loadingBar.style.display = 'flex';
        }
        if (progress) progress.classList.remove('visible');
        this.state.isLoading = true;

        // Clear any existing auto-step timer
        if (this._loadingStepTimer) {
            clearInterval(this._loadingStepTimer);
            this._loadingStepTimer = null;
        }

        if (sequential) {
            // Sequential mode: cycle through LOADING_STEPS automatically
            if (dotsContainer) dotsContainer.style.display = 'flex';
            this._loadingStep = 0;
            this._updateLoadingStep();

            this._loadingStepTimer = setInterval(() => {
                const steps = GraphExplorer.LOADING_STEPS;
                if (this._loadingStep < steps.length - 1) {
                    this._loadingStep++;
                    this._updateLoadingStep();
                }
                // Stay on last step until _hideLoading is called
            }, 1800);
        } else {
            // Simple mode: show a single static message
            if (dotsContainer) dotsContainer.style.display = 'none';
            if (loadingText) loadingText.textContent = text || 'Loading...';
            dots.forEach(d => { d.classList.remove('active', 'done'); });
        }
    }

    /** Update spinner text + step dots to the current _loadingStep */
    _updateLoadingStep() {
        const steps = GraphExplorer.LOADING_STEPS;
        const loadingText = this.container.querySelector('#loadingBar-text');
        const dots = this.container.querySelectorAll('.loading-step-dot');
        if (loadingText) loadingText.textContent = steps[this._loadingStep] || 'Loading...';
        dots.forEach((dot, i) => {
            dot.classList.toggle('done', i < this._loadingStep);
            dot.classList.toggle('active', i === this._loadingStep);
        });
    }

    _hideLoading() {
        if (this._loadingStepTimer) {
            clearInterval(this._loadingStepTimer);
            this._loadingStepTimer = null;
        }
        const loadingBar = this.container.querySelector('#loadingBar');
        if (loadingBar) loadingBar.style.display = 'none';
        this.state.isLoading = false;
    }
    
    _updateLoadingProgress(progress, text) {
        const loadingProgress = this.container.querySelector('#loadingBar-progress');
        const loadingText = this.container.querySelector('#loadingBar-text');
        if (loadingProgress) {
            loadingProgress.classList.add('visible');
            loadingProgress.style.setProperty('--progress', `${progress}%`);
        }
        if (loadingText) loadingText.textContent = text;
    }
    
    /**
     * Show a temporary notification message
     * @param {string} message - The message to display
     * @param {string} type - Type: 'success', 'error', 'info', 'warning'
     */
    _showNotification(message, type = 'info') {
        // Remove any existing notification
        const existing = this.container.querySelector('.graph-notification');
        if (existing) existing.remove();
        
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `graph-notification graph-notification-${type}`;
        safeSetInnerHTML(notification, `
            <span class="notification-message">${message}</span>
            <button class="notification-close">&times;</button>
        `);
        
        // Style the notification
        notification.style.cssText = `
            position: absolute;
            top: 60px;
            left: 50%;
            transform: translateX(-50%);
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 1000;
            display: flex;
            align-items: center;
            gap: 10px;
            animation: slideIn 0.3s ease-out;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        `;
        
        // Type-specific colors
        const colors = {
            success: { bg: '#10b981', color: '#fff' },
            error: { bg: '#ef4444', color: '#fff' },
            info: { bg: '#888888', color: '#fff' },
            warning: { bg: '#f59e0b', color: '#fff' }
        };
        const c = colors[type] || colors.info;
        notification.style.backgroundColor = c.bg;
        notification.style.color = c.color;
        
        // Add close handler
        notification.querySelector('.notification-close').addEventListener('click', () => {
            notification.remove();
        });
        
        // Add to container
        this.container.appendChild(notification);
        
        // Auto-remove after 4 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.style.animation = 'slideOut 0.3s ease-in';
                setTimeout(() => notification.remove(), 300);
            }
        }, 4000);
    }
    
    _updateEmptyState() {
        const emptyOverlay = this.container.querySelector('#empty-state-overlay');
        if (emptyOverlay) {
            emptyOverlay.style.display = (this.nodes?.length || 0) === 0 ? 'flex' : 'none';
        }
    }
    
    // ============================================
    // Focus Mode (Dim non-selected nodes)
    // ============================================
    
    /**
     * Apply focus mode - dim non-selected nodes for strong contrast.
     * Optimized: only updates nodes/edges whose state actually changes.
     * @param {Array} selectedNodeIds - Array of selected node IDs
     */
    _applyFocusMode(selectedNodeIds) {
        if (!selectedNodeIds || selectedNodeIds.length === 0) {
            this._clearFocusMode();
            return;
        }
        
        const wasFocused = this.state.focusMode;
        this.state.focusMode = true;
        const dimmedOpacity = this.options.focus_dim_opacity ?? 0.15;
        const dimmedEdgeOpacity = this.options.focus_edge_dim_opacity ?? 0.1;
        
        // Get connected nodes (neighbors of selected nodes)
        const selectedSet = new Set(selectedNodeIds);
        const connectedNodeIds = new Set(selectedNodeIds);
        const connectedEdgeIds = new Set();
        
        selectedNodeIds.forEach(nodeId => {
            try {
                const connectedNodes = this.network.getConnectedNodes(nodeId);
                connectedNodes.forEach(id => connectedNodeIds.add(id));
                const connectedEdges = this.network.getConnectedEdges(nodeId);
                connectedEdges.forEach(id => connectedEdgeIds.add(id));
            } catch(e) { /* node may not exist */ }
        });
        
        // Previous dimmed state for delta detection
        const prevDimmedNodes = this.state._dimmedNodeState || {};
        const prevDimmedEdges = this.state._dimmedEdgeState || {};
        const newDimmedNodes = {};
        const newDimmedEdges = {};
        
        // Compute target state for each node and only emit changed updates
        const nodeUpdates = [];
        this.nodes.forEach(node => {
            // Store original color if first focus
            if (!this.state.originalNodeColors[node.id]) {
                this.state.originalNodeColors[node.id] = {
                    color: node.color,
                    font: node.font,
                    opacity: node.opacity
                };
            }
            
            let targetState;
            if (selectedSet.has(node.id)) {
                targetState = 'selected';
            } else if (connectedNodeIds.has(node.id)) {
                targetState = 'connected';
            } else {
                targetState = 'dimmed';
            }
            newDimmedNodes[node.id] = targetState;
            
            // Skip if state unchanged from previous focus
            if (wasFocused && prevDimmedNodes[node.id] === targetState) return;
            
            if (targetState === 'selected') {
                nodeUpdates.push({
                    id: node.id,
                    opacity: 1,
                    borderWidth: 3,
                    shadow: {
                        enabled: true,
                        color: 'rgba(59, 130, 246, 0.5)',
                        size: 10
                    }
                });
            } else if (targetState === 'connected') {
                nodeUpdates.push({
                    id: node.id,
                    opacity: dimmedOpacity * 2.5,
                    borderWidth: 2,
                    shadow: false
                });
            } else {
                nodeUpdates.push({
                    id: node.id,
                    opacity: dimmedOpacity,
                    borderWidth: 1,
                    shadow: false
                });
            }
        });
        
        // Compute target state for each edge
        const edgeUpdates = [];
        this.edges.forEach(edge => {
            if (!this.state.originalEdgeColors[edge.id]) {
                this.state.originalEdgeColors[edge.id] = {
                    color: edge.color,
                    width: edge.width
                };
            }
            
            const isConnected = connectedEdgeIds.has(edge.id);
            const targetState = isConnected ? 'connected' : 'dimmed';
            newDimmedEdges[edge.id] = targetState;
            
            // Skip if state unchanged
            if (wasFocused && prevDimmedEdges[edge.id] === targetState) return;
            
            if (isConnected) {
                edgeUpdates.push({
                    id: edge.id,
                    color: { color: '#888888', opacity: 0.8 },
                    width: 2
                });
            } else {
                edgeUpdates.push({
                    id: edge.id,
                    color: { opacity: dimmedEdgeOpacity },
                    width: 1
                });
            }
        });
        
        // Store state for next delta
        this.state._dimmedNodeState = newDimmedNodes;
        this.state._dimmedEdgeState = newDimmedEdges;
        
        // Apply only changed updates
        if (nodeUpdates.length > 0) this.nodes.update(nodeUpdates);
        if (edgeUpdates.length > 0) this.edges.update(edgeUpdates);
    }
    
    /**
     * Clear focus mode - restore all nodes to original appearance
     */
    _clearFocusMode() {
        if (!this.state.focusMode) return;
        
        this.state.focusMode = false;
        this.state._dimmedNodeState = {};
        this.state._dimmedEdgeState = {};
        
        // Adaptive shadow: only enable shadows for small graphs
        const nodeCount = this.nodes ? this.nodes.length : 0;
        const shadowEnabled = this.options.node_shadow !== undefined ? this.options.node_shadow : (nodeCount < 500);
        
        // Restore all nodes
        const nodeUpdates = [];
        this.nodes.forEach(node => {
            const original = this.state.originalNodeColors[node.id];
            nodeUpdates.push({
                id: node.id,
                opacity: original?.opacity ?? 1,
                borderWidth: this.options.node_border_width || 2,
                shadow: shadowEnabled
            });
        });
        
        // Restore all edges
        const edgeUpdates = [];
        this.edges.forEach(edge => {
            const original = this.state.originalEdgeColors[edge.id];
            edgeUpdates.push({
                id: edge.id,
                color: original?.color ?? {
                    color: '#7f8c8d',
                    opacity: 1.0
                },
                width: original?.width ?? 1.5
            });
        });
        
        // Apply updates
        if (nodeUpdates.length > 0) this.nodes.update(nodeUpdates);
        if (edgeUpdates.length > 0) this.edges.update(edgeUpdates);
    }
    
    /**
     * Manually focus on specific nodes
     * @param {Array} nodeIds - Array of node IDs to focus on
     */
    focusNodes(nodeIds) {
        if (!nodeIds || nodeIds.length === 0) {
            this._clearFocusMode();
            return this;
        }
        
        this._applyFocusMode(nodeIds);
        
        // Also select the nodes in vis.js
        this.network?.selectNodes(nodeIds);
        this.state.selectedNodes = nodeIds;
        
        return this;
    }
    
    /**
     * Clear focus and selection
     */
    clearFocus() {
        this._clearFocusMode();
        this.network?.selectNodes([]);
        this.state.selectedNodes = [];
        this.state.selectedEdges = [];
        this._emitEvent('selectionChange', { nodes: [], edges: [] });
        return this;
    }
    
    // ============================================
    // Data Management
    // ============================================
    
    setData(nodes, edges) {
        if (!this.nodes || !this.edges) return this;
        
        // Clear focus mode and stored colors
        this._clearFocusMode();
        this.state.originalNodeColors = {};
        this.state.originalEdgeColors = {};
        
        this.nodes.clear();
        this.edges.clear();
        this.nodeMap = {};
        
        // Calculate degree-based sizing
        const nodeDegrees = {};
        edges.forEach(edge => {
            nodeDegrees[edge.from] = (nodeDegrees[edge.from] || 0) + 1;
            nodeDegrees[edge.to] = (nodeDegrees[edge.to] || 0) + 1;
        });
        
        const maxDegree = Math.max(...Object.values(nodeDegrees), 1);
        const minSize = 15, maxSize = 50;
        
        // Process nodes
        nodes.forEach(node => {
            if (node.size === undefined) {
                const degree = nodeDegrees[node.id] || 0;
                node.size = Math.round(minSize + (maxSize - minSize) * (Math.log(degree + 1) / Math.log(maxDegree + 1)));
            }
            
            // Store original color
            if (node.color && !node._originalColor) {
                node._originalColor = typeof node.color === 'string' 
                    ? node.color 
                    : node.color.background || node.color.color;
            }
            
            this.nodeMap[node.id] = node;
        });
        
        // Track whether the graph was empty before this setData call
        const wasEmpty = Object.keys(this.nodeMap).length === 0 || this.nodes.length === 0;

        this.nodes.add(nodes);
        this.edges.add(edges);
        
        this._updateEmptyState();
        this._emitEvent('dataChange', { nodes, edges });
        
        // Only auto-fit on first load (when the graph was empty).
        // Subsequent setData calls (refresh) should NOT move the viewport
        // — the user may have deliberately panned/zoomed somewhere.
        if (wasEmpty && nodes.length > 0) {
            setTimeout(() => {
                this.network?.fit({ animation: true });
                setTimeout(() => this._hideLoading(), 500);
            }, 100);
        } else {
            setTimeout(() => this._hideLoading(), 200);
        }
        
        console.log(`[GraphExplorer] Loaded ${nodes.length} nodes, ${edges.length} edges (wasEmpty=${wasEmpty})`);

        return this;
    }
    
    addData(nodes, edges) {
        if (nodes?.length) {
            nodes.forEach(n => this.nodeMap[n.id] = n);
            this.nodes.add(nodes);
        }
        if (edges?.length) this.edges.add(edges);
        this._updateEmptyState();
        this._emitEvent('dataChange', { nodes: this.nodes.get(), edges: this.edges.get() });
        return this;
    }
    
    getData() {
        return {
            nodes: this.nodes ? this.nodes.get() : [],
            edges: this.edges ? this.edges.get() : []
        };
    }
    
    getSelection() {
        return {
            nodes: this.state.selectedNodes,
            edges: this.state.selectedEdges
        };
    }
    
    // ============================================
    // View Controls
    // ============================================
    
    setTheme(theme) {
        this.options.theme = theme;
        
        const explorer = this.container.querySelector('.graph-explorer');
        if (explorer) {
            explorer.classList.toggle('light-theme', theme === 'light');
            explorer.setAttribute('data-theme', theme);
        }
        
        if (this.network) {
            this.network.setOptions({
                nodes: { font: { color: theme === 'dark' ? '#ffffff' : '#212529' } }
            });
        }
        
        // Keep ExplorerCore in sync: update state.theme and re-apply font colors
        // to all nodes already in the DataSet. Without this, ExplorerCore.getTheme()
        // returns the stale initialization theme and updateNodeFontColors() (called
        // after every graph data load) sets the wrong label color.
        if (typeof window !== 'undefined' && window.ExplorerCore) {
            window.ExplorerCore.state.theme = theme;
            if (typeof window.ExplorerCore.updateNodeFontColors === 'function') {
                window.ExplorerCore.updateNodeFontColors(theme);
            }
        }
        
        this._emitEvent('themeChange', theme);
        return this;
    }
    
    setPhysics(enabled) {
        this.options.physics_enabled = enabled;
        if (this.network) {
            this.network._suppressFit = true;
            this.network.setOptions({ physics: { enabled, stabilization: { fit: false } } });
            this.network._suppressFit = false;
        }
        this._emitEvent('physicsChange', enabled);
        return this;
    }
    
    /**
     * Suppress all network.fit() calls (including vis.js internal ones).
     * Call allowFit() to re-enable. Used when changing physics options
     * to prevent vis.js initPhysics from auto-centering the viewport.
     */
    suppressFit() {
        if (this.network) this.network._suppressFit = true;
        return this;
    }
    
    /**
     * Re-enable network.fit() calls after suppressFit().
     */
    allowFit() {
        if (this.network) this.network._suppressFit = false;
        return this;
    }
    
    resetView() {
        this.network?.fit({ animation: true });
        this.network?.selectNodes([]);
        this._clearFocusMode();
        this.state.selectedNodes = [];
        this.state.selectedEdges = [];
        this._emitEvent('selectionChange', { nodes: [], edges: [] });
        return this;
    }
    
    fit(options = { animation: true }) {
        this.network?.fit(options);
        return this;
    }
    
    // ============================================
    // Chat-Panel Viewport Offset
    // ============================================
    
    /**
     * When the chat panel is open (5 of 12 columns on the right), the
     * visible graph area is only 7 columns wide.  vis.js may still
     * compute its viewport centre using the full canvas width, so
     * fit() and focus() appear off-centre.
     *
     * Returns a negative pixel value (−chatWidth/2) that, when passed
     * as moveTo({ offset: { x } }), shifts the focal point leftward
     * so it lands in the middle of the 7-column graph area.
     * Returns 0 when the chat panel is closed.
     */
    _getChatPanelOffset() {
        const ge = this.container?.querySelector('.graph-explorer')
                 || this.container?.closest('.graph-explorer');
        if (!ge || !ge.classList.contains('chat-open')) return 0;
        const chatPanel = ge.querySelector('.explorer-chat-panel');
        if (!chatPanel || chatPanel.offsetWidth <= 0) return 0;
        return -(chatPanel.offsetWidth / 2);
    }

    // ============================================
    // Physics Solvers
    // ============================================
    
    barnesHut(params = {}) {
        const physicsDefaults = window.ExplorerConstants?.DEFAULT_PHYSICS_OPTIONS?.barnesHut || {};
        const config = {
            gravitationalConstant: params.gravity ?? params.gravitationalConstant ?? physicsDefaults.gravitationalConstant ?? -2000,
            centralGravity: params.centralGravity ?? params.central_gravity ?? physicsDefaults.centralGravity ?? 0.3,
            springLength: params.springLength ?? params.spring_length ?? physicsDefaults.springLength ?? 200,
            springConstant: params.springConstant ?? params.spring_strength ?? physicsDefaults.springConstant ?? 0.04,
            damping: params.damping ?? physicsDefaults.damping ?? 0.2,
            avoidOverlap: params.avoidOverlap ?? params.overlap ?? physicsDefaults.avoidOverlap ?? 0.1
        };
        
        this.options.physics_solver = 'barnesHut';
        this.options.physics_options = this.options.physics_options || {};
        this.options.physics_options.barnesHut = config;
        
        if (this.network) {
            this.network._suppressFit = true;
            this.network.setOptions({ physics: { solver: 'barnesHut', barnesHut: config } });
            this.network._suppressFit = false;
        }
        return this;
    }
    
    repulsion(params = {}) {
        const physicsDefaults = window.ExplorerConstants?.DEFAULT_PHYSICS_OPTIONS?.repulsion || {};
        const config = {
            nodeDistance: params.nodeDistance ?? params.node_distance ?? physicsDefaults.nodeDistance ?? 100,
            centralGravity: params.centralGravity ?? params.central_gravity ?? physicsDefaults.centralGravity ?? 0.2,
            springLength: params.springLength ?? params.spring_length ?? physicsDefaults.springLength ?? 200,
            springConstant: params.springConstant ?? params.spring_strength ?? physicsDefaults.springConstant ?? 0.05,
            damping: params.damping ?? physicsDefaults.damping ?? 0.09
        };
        
        this.options.physics_solver = 'repulsion';
        this.options.physics_options = this.options.physics_options || {};
        this.options.physics_options.repulsion = config;
        
        if (this.network) {
            this.network._suppressFit = true;
            this.network.setOptions({ physics: { solver: 'repulsion', repulsion: config } });
            this.network._suppressFit = false;
        }
        return this;
    }
    
    hrepulsion(params = {}) {
        const physicsDefaults = window.ExplorerConstants?.DEFAULT_PHYSICS_OPTIONS?.hierarchicalRepulsion || {};
        const config = {
            nodeDistance: params.nodeDistance ?? params.node_distance ?? physicsDefaults.nodeDistance ?? 120,
            centralGravity: params.centralGravity ?? params.central_gravity ?? physicsDefaults.centralGravity ?? 0.0,
            springLength: params.springLength ?? params.spring_length ?? physicsDefaults.springLength ?? 100,
            springConstant: params.springConstant ?? params.spring_strength ?? physicsDefaults.springConstant ?? 0.01,
            damping: params.damping ?? physicsDefaults.damping ?? 0.09
        };
        
        this.options.physics_solver = 'hierarchicalRepulsion';
        this.options.physics_options = this.options.physics_options || {};
        this.options.physics_options.hierarchicalRepulsion = config;
        
        if (this.network) {
            this.network._suppressFit = true;
            this.network.setOptions({ physics: { solver: 'hierarchicalRepulsion', hierarchicalRepulsion: config } });
            this.network._suppressFit = false;
        }
        return this;
    }
    
    forceAtlas2Based(params = {}) {
        const physicsDefaults = window.ExplorerConstants?.DEFAULT_PHYSICS_OPTIONS?.forceAtlas2Based || {};
        const config = {
            gravitationalConstant: params.gravity ?? params.gravitationalConstant ?? physicsDefaults.gravitationalConstant ?? -50,
            centralGravity: params.centralGravity ?? params.central_gravity ?? physicsDefaults.centralGravity ?? 0.01,
            springLength: params.springLength ?? params.spring_length ?? physicsDefaults.springLength ?? 100,
            springConstant: params.springConstant ?? params.spring_strength ?? physicsDefaults.springConstant ?? 0.08,
            damping: params.damping ?? physicsDefaults.damping ?? 0.4,
            avoidOverlap: params.avoidOverlap ?? params.overlap ?? physicsDefaults.avoidOverlap ?? 0
        };
        
        this.options.physics_solver = 'forceAtlas2Based';
        this.options.physics_options = this.options.physics_options || {};
        this.options.physics_options.forceAtlas2Based = config;
        
        if (this.network) {
            this.network._suppressFit = true;
            this.network.setOptions({ physics: { solver: 'forceAtlas2Based', forceAtlas2Based: config } });
            this.network._suppressFit = false;
        }
        return this;
    }
    
    // ============================================
    // Layout Configuration
    // ============================================
    
    setHierarchical(options = {}) {
        const config = {
            enabled: true,
            levelSeparation: options.levelSeparation ?? options.level_separation ?? 150,
            treeSpacing: options.treeSpacing ?? options.tree_spacing ?? 200,
            blockShifting: options.blockShifting ?? options.block_shifting ?? true,
            edgeMinimization: options.edgeMinimization ?? options.edge_minimization ?? true,
            parentCentralization: options.parentCentralization ?? options.parent_centralization ?? true,
            direction: options.direction ?? 'UD',
            sortMethod: options.sortMethod ?? options.sort_method ?? 'hubsize'
        };
        
        this.options.layout = 'hierarchical';
        this.options.hierarchical = config;
        
        if (this.network) {
            this.network.setOptions({ layout: { hierarchical: config } });
        }
        return this;
    }
    
    disableHierarchical() {
        this.options.layout = null;
        if (this.options.hierarchical) this.options.hierarchical.enabled = false;
        
        if (this.network) {
            this.network.setOptions({ layout: { hierarchical: { enabled: false } } });
        }
        return this;
    }
    
    // ============================================
    // Edge Configuration
    // ============================================
    
    setEdgeSmooth(smoothType) {
        this.options.edge_smooth = smoothType;
        if (this.network) {
            this.network.setOptions({ edges: { smooth: { enabled: true, type: smoothType } } });
        }
        return this;
    }
    
    inheritEdgeColors(status) {
        this.options.inherit_edge_colors = status;
        if (this.network) {
            this.network.setOptions({ edges: { color: { inherit: status } } });
        }
        return this;
    }
    
    // ============================================
    // vis.js Configure Panel
    // ============================================
    
    showButtons(filter = true) {
        const configOptions = { enabled: true };
        
        if (Array.isArray(filter)) {
            configOptions.filter = (option, path) => filter.some(f => path.indexOf(f) !== -1);
        }
        
        if (this.network) {
            this.network.setOptions({ configure: configOptions });
        }
        return this;
    }
    
    hideButtons() {
        if (this.network) {
            this.network.setOptions({ configure: { enabled: false } });
        }
        return this;
    }
    
    // ============================================
    // Direct vis.js Options
    // ============================================
    
    setOptions(options) {
        let parsed = options;
        if (typeof options === 'string') {
            try {
                parsed = JSON.parse(options.replace(/'/g, '"'));
            } catch (e) {
                console.error('[GraphExplorer] Failed to parse options:', e);
                return this;
            }
        }
        if (this.network) {
            this.network.setOptions(parsed);
        }
        return this;
    }
    
    // ============================================
    // Serialization
    // ============================================
    
    toJSON() {
        return {
            nodes: this.nodes?.get() || [],
            edges: this.edges?.get() || [],
            options: this.options
        };
    }
    
    // ============================================
    // Cleanup
    // ============================================
    
    destroy() {
        this._emitEvent('destroy');
        
        if (this.network) {
            this.network.destroy();
            this.network = null;
        }
        
        this.nodes = null;
        this.edges = null;
        this.nodeMap = {};
        safeSetInnerHTML(this.container, '');
    }
}

export default GraphExplorer;
